import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { ShieldCheck, Loader2, PenLine } from 'lucide-react';
import { SignaturePad } from './SignaturePad';
import api from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';

/**
 * Blocking modal shown on first login for agents/admins.
 * Prevents app usage until the confidentiality clause is signed.
 */
export const ConfidentialitySignGate = () => {
  const { user, isAuthenticated } = useAuth();
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [accepted, setAccepted] = useState(false);
  const [signatureData, setSignatureData] = useState(null);
  const [showPad, setShowPad] = useState(false);

  useEffect(() => {
    const check = async () => {
      if (!isAuthenticated || !user) return;
      const role = user.role;
      if (!['agent', 'admin', 'super_admin'].includes(role)) return;
      try {
        const res = await api.get('/confidentiality/status');
        if (res.data.required && !res.data.signed) {
          setStatus(res.data);
          setOpen(true);
        }
      } catch {
        // silent - endpoint not deployed or network error
      }
    };
    check();
  }, [isAuthenticated, user]);

  const handleSign = async () => {
    if (!accepted) {
      toast.error("Veuillez cocher la case d'acceptation");
      return;
    }
    if (!signatureData) {
      toast.error('Veuillez apposer votre signature');
      return;
    }
    setSubmitting(true);
    try {
      await api.post('/confidentiality/sign', { signature_data: signatureData });
      toast.success('Clause de confidentialite signee. Merci.');
      setOpen(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la signature');
    } finally {
      setSubmitting(false);
    }
  };

  if (!open || !status) return null;

  // Resolve placeholder for display
  const content = (status.clause_content || '').replace('{{user_name}}', user?.name || '');

  return (
    <Dialog open={open} onOpenChange={() => {}}>
      <DialogContent
        className="sm:max-w-[720px] max-h-[92vh] overflow-hidden flex flex-col"
        onPointerDownOutside={e => e.preventDefault()}
        onEscapeKeyDown={e => e.preventDefault()}
        data-testid="confidentiality-modal"
      >
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#1B3A4B] flex items-center justify-center flex-shrink-0">
              <ShieldCheck className="w-6 h-6 text-[#D4A745]" />
            </div>
            <div>
              <DialogTitle className="text-xl text-[#1B3A4B]">{status.clause_title}</DialogTitle>
              <DialogDescription className="text-sm">
                Signature obligatoire avant acces a l'application
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto px-1 space-y-4 min-h-0">
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-900">
            <b>{user?.name}</b>, en tant que <b>{user?.role}</b> de l'agence, vous devez signer cette clause
            de confidentialite pour poursuivre. Une attestation PDF sera generee et conservee.
          </div>

          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 max-h-[260px] overflow-y-auto">
            <pre className="text-xs text-gray-700 whitespace-pre-wrap font-sans leading-relaxed" data-testid="clause-content">
              {content}
            </pre>
          </div>

          <div className="flex items-start gap-2">
            <Checkbox
              id="accept-clause"
              checked={accepted}
              onCheckedChange={setAccepted}
              data-testid="accept-clause-checkbox"
              className="mt-0.5"
            />
            <Label htmlFor="accept-clause" className="text-sm text-gray-700 leading-relaxed cursor-pointer">
              J'ai lu et je reconnais avoir pris connaissance de la clause de confidentialite dans son integralite.
              Je m'engage a en respecter tous les termes.
            </Label>
          </div>

          <div>
            <Label className="text-sm font-medium text-gray-700 mb-2 block">
              Signature <span className="text-red-500">*</span>
            </Label>
            {signatureData ? (
              <div className="border-2 border-green-200 bg-green-50 rounded-lg p-3 flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <img src={signatureData} alt="Signature" className="h-12 bg-white border rounded" />
                  <span className="text-sm text-green-700 font-medium">Signature capturee</span>
                </div>
                <Button variant="ghost" size="sm" onClick={() => { setSignatureData(null); setShowPad(true); }} data-testid="resign-btn">
                  Refaire
                </Button>
              </div>
            ) : showPad ? (
              <SignaturePad
                width={640}
                height={180}
                onSave={(dataUrl) => { setSignatureData(dataUrl); setShowPad(false); }}
                onCancel={() => setShowPad(false)}
              />
            ) : (
              <Button
                type="button"
                variant="outline"
                className="w-full border-dashed border-2 h-24 text-gray-500 hover:border-[#1B3A4B] hover:text-[#1B3A4B]"
                onClick={() => setShowPad(true)}
                data-testid="open-signature-pad-btn"
              >
                <PenLine className="w-5 h-5 mr-2" />
                Cliquez pour apposer votre signature
              </Button>
            )}
          </div>
        </div>

        <div className="pt-3 border-t flex flex-col sm:flex-row gap-2 sm:justify-end flex-shrink-0">
          <Button
            onClick={handleSign}
            disabled={!accepted || !signatureData || submitting}
            className="bg-[#1B3A4B] hover:bg-[#142D3A] min-w-[200px]"
            data-testid="sign-clause-submit"
          >
            {submitting ? (
              <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Enregistrement...</>
            ) : (
              <>Je signe et j'accepte</>
            )}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
